import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { History } from "lucide-react";

import type { ActionCard } from "@/types/actions";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Modal } from "@/components/ui/modal";
import { Skeleton } from "@/components/ui/skeleton";
import { ActionQueueLabels } from "@/lib/action-queue";
import { djs } from "@/lib/djs";
import type { ActionHistoryEvent } from "@/types/actions";

import { EvidenceModal } from "./evidence-modal";

export function ActionHistoryModal({
	action,
	onClose,
}: {
	action: ActionCard;
	onClose: () => void;
}) {
	const { data, isPending, isError } = useQuery(
		orpc.today.history.queryOptions({ input: { id: action.id } }),
	);
	const [evidence, setEvidence] = useState<ActionHistoryEvent | null>(null);

	if (evidence !== null) {
		return (
			<EvidenceModal
				personId={action.person_id}
				sources={evidence.sources}
				title={ActionQueueLabels.event[evidence.type]}
				onClose={() => setEvidence(null)}
			/>
		);
	}

	return (
		<Modal
			title="histórico da pendência"
			description={action.task}
			onClose={onClose}
			className="max-w-2xl"
			component="action-history"
		>
			{isPending && (
				<div role="status" aria-label="carregando histórico">
					<Skeleton className="h-28 w-full" />
				</div>
			)}
			{isError && (
				<p role="alert" className="text-sm text-destructive">
					não foi possível carregar o histórico.
				</p>
			)}
			{data?.length === 0 && <EmptyState icon={History} title="nenhuma decisão registrada" />}
			{data !== undefined && data.length > 0 && (
				<ol className="flex flex-col gap-4">
					{data.map((event) => (
						<li key={event.id} className="border-l-2 border-border pl-4">
							<div className="flex flex-wrap items-baseline justify-between gap-2">
								<p className="text-sm font-medium text-foreground">
									{ActionQueueLabels.event[event.type]}
								</p>
								<time
									dateTime={event.created_at.toISOString()}
									className="text-xs text-muted-foreground"
								>
									{djs(event.created_at).tz("America/Sao_Paulo").format("DD/MM/YYYY HH:mm")}
								</time>
							</div>
							<p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{event.note}</p>
							<p className="mt-1 text-xs text-muted-foreground">
								{ActionQueueLabels.role[event.actor]}
							</p>
							{event.type === "transferred" && (
								<p className="mt-1 text-xs text-muted-foreground">
									responsável: {ActionQueueLabels.role[event.role]}
								</p>
							)}
							{event.sources.length > 0 && (
								<Button
									type="button"
									size="sm"
									variant="ghost"
									className="mt-1 px-0 text-primary"
									onClick={() => setEvidence(event)}
								>
									ver fontes
								</Button>
							)}
						</li>
					))}
				</ol>
			)}
		</Modal>
	);
}
